(() => {
  const ROOT_SELECTOR = '[data-tb-infinite-scroll="true"]';
  const AUTO_LOAD_LIMIT = 1;
  const instances = new WeakMap();

  const addStyles = () => {
    if (document.getElementById('tb-infinite-scroll-styles')) return;
    const style = document.createElement('style');
    style.id = 'tb-infinite-scroll-styles';
    style.textContent = `
      .tb-infinite-scroll--active .pagination { display: none; }
      .tb-infinite-scroll__status {
        min-height: 0;
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: center;
        gap: 10px;
        margin: 20px 0 6px;
        font-size: 12px;
        letter-spacing: .12em;
        text-transform: uppercase;
        text-align: center;
      }
      .tb-infinite-scroll__status[hidden] { display: none !important; }
      .tb-infinite-scroll__spinner {
        width: 18px;
        height: 18px;
        border: 1px solid currentColor;
        border-right-color: transparent;
        border-radius: 50%;
        animation: tbInfiniteSpin .7s linear infinite;
      }
      .tb-infinite-scroll__status:not(.is-loading) .tb-infinite-scroll__spinner { display: none; }
      .tb-infinite-scroll__load-more[hidden] { display: none; }
      .tb-infinite-scroll__load-more { margin: 0; min-width: 190px; }
      .tb-infinite-scroll__sentinel { width: 100%; height: 1px; }
      @keyframes tbInfiniteSpin { to { transform: rotate(360deg); } }
      @media (prefers-reduced-motion: reduce) {
        .tb-infinite-scroll__spinner { animation-duration: 1.5s; }
      }
    `;
    document.head.appendChild(style);
  };

  const getGrid = (root) => root.querySelector('.collection-grid__wrapper > .grid, .collection-grid__wrapper .grid.grid--uniform');
  const getPagination = (root) => root.querySelector('.pagination');
  const getNextUrl = (root) => {
    const link = root.querySelector('.pagination .next a');
    return link ? new URL(link.getAttribute('href'), window.location.origin).href : null;
  };

  class TaelorInfiniteScroll {
    constructor(root) {
      this.root = root;
      this.grid = getGrid(root);
      this.pagination = getPagination(root);
      this.nextUrl = getNextUrl(root);
      this.loading = false;
      this.finished = false;
      this.autoLoads = 0;
      this.manualMode = false;
      this.observer = null;
      this.abortController = new AbortController();
      this.setup();
    }

    setup() {
      if (!this.grid || !this.nextUrl) return;
      addStyles();
      this.root.classList.add('tb-infinite-scroll--active');

      this.status = document.createElement('div');
      this.status.className = 'tb-infinite-scroll__status';
      this.status.hidden = true;
      this.status.setAttribute('role', 'status');
      this.status.setAttribute('aria-live', 'polite');
      this.status.innerHTML = '<span class="tb-infinite-scroll__spinner" aria-hidden="true"></span><span class="tb-infinite-scroll__message"></span><button type="button" class="btn btn--small tb-infinite-scroll__load-more" hidden>Load more products</button>';
      this.loadMoreButton = this.status.querySelector('.tb-infinite-scroll__load-more');

      this.sentinel = document.createElement('div');
      this.sentinel.className = 'tb-infinite-scroll__sentinel';
      this.sentinel.setAttribute('aria-hidden', 'true');

      if (this.pagination) {
        this.pagination.before(this.status);
        this.status.after(this.sentinel);
      } else {
        this.grid.after(this.status, this.sentinel);
      }

      this.observer = new IntersectionObserver((entries) => {
        if (!this.manualMode && entries.some((entry) => entry.isIntersecting)) this.loadNext(false);
      }, { rootMargin: '1100px 0px 1100px 0px' });
      this.observer.observe(this.sentinel);

      if (this.loadMoreButton) {
        this.loadMoreButton.addEventListener('click', () => this.loadNext(true), { signal: this.abortController.signal });
      }

      this.root.addEventListener('click', (event) => {
        const productLink = event.target.closest('.grid-product__link');
        if (productLink) {
          try {
            sessionStorage.setItem('tbCollectionScroll:' + window.location.pathname + window.location.search, String(window.scrollY));
          } catch (e) {}
        }
      }, { signal: this.abortController.signal });
    }

    setStatus(message, loading = false, visible = true) {
      if (!this.status) return;
      this.status.hidden = !visible;
      this.status.classList.toggle('is-loading', loading);
      const messageEl = this.status.querySelector('.tb-infinite-scroll__message');
      if (messageEl) messageEl.textContent = message || '';
      if (this.loadMoreButton) this.loadMoreButton.hidden = true;
    }

    hideStatus() {
      this.setStatus('', false, false);
    }

    showLoadMore() {
      this.manualMode = true;
      if (this.observer) this.observer.disconnect();
      this.setStatus('', false, true);
      if (this.loadMoreButton) this.loadMoreButton.hidden = false;
    }

    async loadNext(manual = false) {
      if (this.loading || this.finished || !this.nextUrl) return;
      this.loading = true;
      this.setStatus('Loading more products', true, true);

      try {
        const response = await fetch(this.nextUrl, {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
        if (!response.ok) throw new Error('Unable to load next collection page');

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const sourceRoot = doc.querySelector(ROOT_SELECTOR) || doc.querySelector('.collection-content');
        if (!sourceRoot) throw new Error('Collection content missing');

        const nextGrid = getGrid(sourceRoot);
        if (!nextGrid) throw new Error('Product grid missing');

        const existingIds = new Set(
          Array.from(this.grid.querySelectorAll('[data-product-id]')).map((el) => el.getAttribute('data-product-id'))
        );
        const items = Array.from(nextGrid.children).filter((node) => node.classList && node.classList.contains('grid__item'));
        let appended = 0;

        items.forEach((node) => {
          const id = node.getAttribute('data-product-id');
          if (id && existingIds.has(id)) return;
          this.grid.appendChild(node);
          if (id) existingIds.add(id);
          appended += 1;
        });

        const nextPagination = getPagination(sourceRoot);
        if (this.pagination && nextPagination) this.pagination.innerHTML = nextPagination.innerHTML;
        this.nextUrl = nextPagination ? getNextUrl(sourceRoot) : null;
        if (!manual) this.autoLoads += 1;

        if (window.AOS && typeof window.AOS.refreshHard === 'function') {
          window.AOS.refreshHard();
        } else if (window.AOS && typeof window.AOS.refresh === 'function') {
          window.AOS.refresh();
        }

        document.dispatchEvent(new CustomEvent('taelor:collection-products-appended', {
          detail: { count: appended, root: this.root }
        }));

        if (!this.nextUrl || appended === 0) {
          this.finish();
        } else if (manual || this.manualMode || this.autoLoads >= AUTO_LOAD_LIMIT) {
          this.showLoadMore();
        } else {
          this.hideStatus();
        }
      } catch (error) {
        console.warn('[Taelor] Infinite collection loading stopped:', error);
        this.root.classList.remove('tb-infinite-scroll--active');
        this.hideStatus();
        if (this.observer) this.observer.disconnect();
      } finally {
        this.loading = false;
      }
    }

    finish() {
      this.finished = true;
      this.nextUrl = null;
      if (this.observer) this.observer.disconnect();
      if (this.sentinel) this.sentinel.remove();
      if (this.pagination) this.pagination.style.display = 'none';
      if (this.loadMoreButton) this.loadMoreButton.hidden = true;
      this.setStatus("You've reached the end", false, true);
    }

    disconnect() {
      if (this.observer) this.observer.disconnect();
      this.abortController.abort();
      if (this.status) this.status.remove();
      if (this.sentinel) this.sentinel.remove();
      this.root.classList.remove('tb-infinite-scroll--active');
    }
  }

  const scan = () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      const grid = getGrid(root);
      if (!grid) return;
      const existing = instances.get(root);
      if (!existing || existing.grid !== grid) {
        if (existing) existing.disconnect();
        instances.set(root, new TaelorInfiniteScroll(root));
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan, { once: true });
  } else {
    scan();
  }

  let scanQueued = false;
  const mutationObserver = new MutationObserver(() => {
    if (scanQueued) return;
    scanQueued = true;
    requestAnimationFrame(() => {
      scanQueued = false;
      scan();
    });
  });
  mutationObserver.observe(document.documentElement, { childList: true, subtree: true });
})();